import { HttpParams } from "@angular/common/http";
import { forwardRef, Inject, Injectable, QueryList } from "@angular/core";
import { Moment } from "moment";
import { firstValueFrom } from "rxjs";
import { USER_EVENTS_V2, USER_HISTORICAL_EVENTS_V2 } from "../../common/endpoints";
import { Alert, AlertSeverity, Metric, MetricRange, PagedList, Thing, Value, ValueRangeSeverity } from "../../model";
import { AuthenticationService } from "../../service/authentication.service";
import { ColorManagerService } from "../../service/color-manager.service";
import { DataService } from "../../service/data.service";
import { FilterService } from "../../service/filter.service";
import { HttpService } from "../../service/http.service";
import { MetricService } from "../../service/metric.service";
import { AbstractThingContextService } from "../../shared/class/abstract-thing-context-service.class";
import { MetricDetailComponent } from "../../shared/component";
import { LocalizationPipe } from "../../shared/pipe";
import { EventSeverityPipe } from "../../shared/pipe/event-severity.pipe";
import { EventListWidgeService } from "../alert-list-v2/event-list/event-list-widget.service";
import { ChartData, ChartDataValue, DailySummaryGridWidgetConfig, DailySummaryGridWidgetHighlightType, DailySummaryGridWidgetMetricConfig, MetricData, MetricDetails, XAxisValue, YAxisValue } from "./daily-summary-grid-widget.component";

@Injectable()
export class DailySummaryGridWidgetService {

    private defaultColor: string;;
    private emptyCellColor: string;
    private metricHighlightColor: string;
    private metricDynamicRangeValues: { [metricId: string]: any };
    private severityColorCache: { [variable: string]: string } = {};

    // Element from which the CSS variables are resolved, set by the widget
    styleElement: HTMLElement;

    constructor(
        @Inject(forwardRef(() => AuthenticationService)) private authenticationService: AuthenticationService,
        @Inject(forwardRef(() => LocalizationPipe)) private localizationPipe: LocalizationPipe,
        @Inject(forwardRef(() => EventListWidgeService)) private eventListWidgeService: EventListWidgeService,
        @Inject(forwardRef(() => HttpService)) private httpService: HttpService,
        @Inject(forwardRef(() => EventSeverityPipe)) private eventSeverityPipe: EventSeverityPipe,
        @Inject(forwardRef(() => AbstractThingContextService)) private thingContextService: AbstractThingContextService,
        @Inject(forwardRef(() => FilterService)) private filterService: FilterService,
        @Inject(forwardRef(() => DataService)) private dataService: DataService,
        @Inject(forwardRef(() => ColorManagerService)) private colorManagerService: ColorManagerService
    ) {
        this.initDefaultColors();
    }

    private initDefaultColors(): void {
        this.defaultColor = this.colorManagerService.getColor('CHART_NEUTRAL') || '#212121';
        this.emptyCellColor = this.colorManagerService.getColor('CHART_FILL') || '#fafafa';
        this.metricHighlightColor = this.colorManagerService.getColor('CHART_NEUTRAL') || '#212121';
    }

    computeXAxisValues(start: Moment, end: Moment): XAxisValue[] {
        let results: XAxisValue[] = [];
        const daysBetween = end.diff(start, 'days');
        const startDayMoment: Moment = start.startOf('day');
        for (let i = 0; i <= daysBetween; i++) {
            let moment = startDayMoment.clone().add(i, 'days');
            results.push({ startTimestamp: moment.valueOf(), endTimestamp: moment.endOf('day').valueOf() });
        }
        return results;
    }

    loadEventData(advancedSearchBody: any, xAxisValues: XAxisValue[], config: DailySummaryGridWidgetConfig): Promise<{ data: ChartData[], yAxisValues: YAxisValue[] }> {
        let promises = [];
        let activeAdvancedSearch = Object.assign({}, advancedSearchBody);
        delete activeAdvancedSearch.minEndTimestamp;
        promises.push(this.getRecursivelyAllEvents(null, activeAdvancedSearch, true, null));    // active
        promises.push(this.getRecursivelyAllEvents(null, advancedSearchBody, false, null));   // historical
        return Promise.all(promises).then(results => {
            let allEvents = results.flat();
            return this.handleEventData(allEvents, xAxisValues, config);
        });
    }

    private handleEventData(events: Alert[], xAxisValues: XAxisValue[], config: DailySummaryGridWidgetConfig): { data: ChartData[], yAxisValues: YAxisValue[] } {
        if (!events.length) {
            return { data: [], yAxisValues: [] };
        }
        let eventDataMap: { [category: string]: { [dateTimestamp: string]: EventDataMapValue } } = {};
        events.forEach(event => {
            const dateTimestamps = xAxisValues.filter(d => (event.startTimestamp >= d.startTimestamp && event.startTimestamp <= d.endTimestamp) ||
                (!event.endTimestamp && d.startTimestamp > event.startTimestamp) ||
                (event.endTimestamp >= d.startTimestamp && event.endTimestamp <= d.endTimestamp) ||
                (event.startTimestamp < d.startTimestamp && event.endTimestamp > d.endTimestamp)
            ).map(d => { return d.startTimestamp });
            const category = event.name;
            if (!eventDataMap[category]) {      // empty data points
                eventDataMap[category] = {};
                xAxisValues.forEach(date => {
                    eventDataMap[category][date.startTimestamp] = { event: event, highlightCount: 0, events: [] };
                });
            }
            dateTimestamps.forEach(dateTimestamp => {
                let eventDataMapValue = eventDataMap[category][dateTimestamp];
                eventDataMapValue.highlightCount++;
                eventDataMapValue.events.push(event);
            });
        });
        let data: ChartData[] = [];
        let categoryMap: { [category: string]: { count: number, label: string } } = {};
        Object.keys(eventDataMap).forEach(category => {
            Object.keys(eventDataMap[category]).forEach(dateTimestamp => {
                const color = this.getEventColor(eventDataMap[category][dateTimestamp], config);
                const tooltipText = this.getEventTooltipText(eventDataMap[category][dateTimestamp]);
                const dataValue: ChartDataValue = { color: color, highlightCount: eventDataMap[category][dateTimestamp].highlightCount, tooltipText: tooltipText, events: eventDataMap[category][dateTimestamp].events, metricData: null, metricDetailsArray: null };
                data.push({ category: category, dateTimestamp: dateTimestamp, value: dataValue, categoryLabel: eventDataMap[category][dateTimestamp].event.title || eventDataMap[category][dateTimestamp].event.name });
                if (!categoryMap[category]) {
                    categoryMap[category] = { count: eventDataMap[category][dateTimestamp].highlightCount, label: eventDataMap[category][dateTimestamp].event.title || eventDataMap[category][dateTimestamp].event.name };
                } else {
                    categoryMap[category].count += eventDataMap[category][dateTimestamp].highlightCount;
                }
            });
        });
        const sortedCategories = this.getSortedCategories(categoryMap, config);
        return { data: data, yAxisValues: sortedCategories };
    }

    private getEventColor(value: EventDataMapValue, config: DailySummaryGridWidgetConfig): string {
        if (value.highlightCount > 0) {
            if (config?.showEventSeverity) {
                switch (value.event.severity) {
                    case AlertSeverity.EMERGENCY:
                        return this.getSeverityColor('--severities-critical', this.colorManagerService.getColor('EMERGENCY') || '#dd4b39');
                    case AlertSeverity.CRITICAL:
                        return this.getSeverityColor('--severities-critical', this.colorManagerService.getColor('CRITICAL') || '#dd4b39');
                    case AlertSeverity.FAILURE:
                        return this.getSeverityColor('--severities-critical', this.colorManagerService.getColor('FAILURE') || '#dd4b39');
                    case AlertSeverity.WARNING:
                        return this.getSeverityColor('--severities-warning', this.colorManagerService.getColor('WARNING') || '#f39c12');
                    case AlertSeverity.INFORMATIONAL:
                        return this.getSeverityColor('--severities-success', this.colorManagerService.getColor('INFORMATIONAL') || '#00a65a');
                    default:
                        return this.defaultColor;
                }
            } else {
                return this.defaultColor;
            }
        } else {
            return this.emptyCellColor;
        }
    }

    private getEventTooltipText(value: EventDataMapValue): string {
        if (value.highlightCount > 0) {
            let tooltipParts = [];
            if (value.event.title) {
                tooltipParts.push(this.localizationPipe.transform(value.event.title));
            }
            tooltipParts.push(this.localizationPipe.transform(this.eventSeverityPipe.transform(value.event['eventSeverity'], true)));
            if (value.highlightCount > 1) {
                tooltipParts.push(this.localizationPipe.transform('Count') + ': ' + value.highlightCount);
            }
            return tooltipParts.join('\n');
        } else {
            return null;
        }
    }

    private getSortedCategories(categoryMap: { [category: string]: { count: number, label: string } }, config: DailySummaryGridWidgetConfig): YAxisValue[] {
        if (config?.sortByOccurrences) {
            return Object.entries(categoryMap).sort((a, b) => b[1].count - a[1].count).map(entry => { return { category: entry[0], categoryLabel: entry[1].label } });
        } else {
            return Object.entries(categoryMap).sort((a, b) => (a[1].label > b[1].label) ? 1 : -1).map(entry => { return { category: entry[0], categoryLabel: entry[1].label } });
        }
    }

    private getRecursivelyAllEvents(page: number, advancedSearchBody: any, isActive: boolean, events: Alert[]): Promise<Alert[]> {
        if (!page) {
            page = 0;
        }
        if (!events) {
            events = [];
        }
        return this.getEventPagedList(page, 200, advancedSearchBody, isActive)
            .then(pagedEvents => {
                events = events.concat(pagedEvents.content);
                if (pagedEvents.last) {
                    return events;
                } else {
                    return this.getRecursivelyAllEvents(++page, advancedSearchBody, isActive, events);
                }
            });
    }

    private getEventPagedList(page: number, size: number, advancedSearchBody: any, isActive: boolean): Promise<PagedList<Alert>> {
        let params = this.eventListWidgeService.getParams(null, advancedSearchBody, false, false);
        params = params.set('page', page + '');
        params = params.set('size', size + '');
        return firstValueFrom(this.httpService.get<PagedList<Alert>>(isActive ? USER_EVENTS_V2 : USER_HISTORICAL_EVENTS_V2, params));
    }

    loadMetricData(xAxisValues: XAxisValue[], metricComponents: QueryList<MetricDetailComponent>, startTimestamp: number, endTimestamp: number, config: DailySummaryGridWidgetConfig): Promise<{ data: ChartData[], yAxisValues: YAxisValue[] }> {
        let params = new HttpParams();
        params = params.set('startDate', startTimestamp);
        params = params.set('endDate', endTimestamp);
        return this.thingContextService.getMetrics().then(thingMetrics => {
            let validMetrics: { label: string, metric: Metric, config: DailySummaryGridWidgetMetricConfig, unit: string, filter: any, filterArg: any }[] = [];
            metricComponents?.forEach(metricComponent => {
                const metric = thingMetrics.find(m => m.name === metricComponent.name);
                if (metric && !validMetrics.some(m => m.metric.name == metric.name)) {
                    validMetrics.push({ label: metricComponent.label || metric.label || metric.name, metric: metric, config: metricComponent.config, unit: !this.filterService.isUnitAware(metricComponent.filter) ? (metricComponent.unit != null ? metricComponent.unit : metric.unit) : null, filter: MetricService.getMetricFilter(metricComponent), filterArg: { metric: metric, templateElement: metricComponent.getTemplateInputMap() } });
                } else {
                    console.error(`Invalid metric ${metricComponent.name}`);
                }
            });
            if (!validMetrics.length) {
                return Promise.resolve({ data: [], yAxisValues: [] });
            }
            let promises = [];
            let previousValuesMap: { [metricName: string]: Value } = {};
            let metricValuesMap: { [metricName: string]: Value[] } = {};
            this.metricDynamicRangeValues = {};
            const currentThing = this.thingContextService.getCurrentThing();
            validMetrics.forEach(m => {
                const metricName = m.metric.name;
                promises.push(this.dataService.getValues(metricName, currentThing.id, null, params).then(result => metricValuesMap[metricName] = result.values.reverse()));
                promises.push(this.dataService.getLastValueByThingIdAndMetricName(currentThing.id, metricName, new HttpParams().set('endDate', startTimestamp - 1)).then(result => previousValuesMap[metricName] = result));
                promises.push(this.setMetricRangeDynamicValues(m.metric, thingMetrics, currentThing));
            });
            return Promise.all(promises).then(() => {
                return this.handleMetricData(xAxisValues, validMetrics, metricValuesMap, previousValuesMap, config);
            });
        });
    }

    private handleMetricData(xAxisValues: XAxisValue[], metrics: { label: string, metric: Metric, config: DailySummaryGridWidgetMetricConfig, unit: string, filter: any, filterArg: any }[], metricValuesMap: { [metricName: string]: Value[] }, previousValuesMap: { [metricName: string]: Value }, config: DailySummaryGridWidgetConfig): { data: ChartData[], yAxisValues: YAxisValue[] } {
        let metricDataMap: { [group: string]: { [dateTimestamp: string]: MetricDataMapValue } } = {};
        metrics.forEach(m => {
            const metricName = m.metric.name;
            const group = m.config?.highlight == DailySummaryGridWidgetHighlightType.VALUE_SEVERITY ? m.label : (m.config?.group || m.metric.group || m.label);
            if (!metricDataMap[group]) {      // empty data points
                metricDataMap[group] = {};
                xAxisValues.forEach(date => {
                    metricDataMap[group][date.startTimestamp] = { metricTooltipInfoMap: {}, colorSeverity: MetricDataColorSeverity.NO_SEVERITY, metricData: [], metricDetailsArray: [{ name: metricName, label: m.label, unit: m.unit, filter: m.filter, filterArg: m.filterArg }] };
                });
            } else {
                xAxisValues.forEach(date => {
                    metricDataMap[group][date.startTimestamp].metricDetailsArray.push({ name: metricName, label: m.label, unit: m.unit, filter: m.filter, filterArg: m.filterArg });
                });
            }
            let previousValue = previousValuesMap[metricName];
            metricValuesMap[metricName].forEach(dataValue => {
                const severity = this.getSeverity(m.metric, dataValue.value, this.metricDynamicRangeValues);
                const dateAxisValue = xAxisValues.find(xAxisVal => xAxisVal.startTimestamp <= dataValue.timestamp && xAxisVal.endTimestamp >= dataValue.timestamp);
                if (dateAxisValue) {
                    let metricDataMapValue = metricDataMap[group][dateAxisValue.startTimestamp];
                    if (this.isMetricDataHighlight(dataValue, previousValue, severity, m.config)) {
                        if (!metricDataMapValue.metricTooltipInfoMap[metricName]) {
                            metricDataMapValue.metricTooltipInfoMap[metricName] = { highlightCount: 1, label: m.label }
                        } else {
                            metricDataMapValue.metricTooltipInfoMap[metricName].highlightCount++;
                        }
                        const metricDataColorSeverity = this.getMetricDataColorSeverity(severity, m.config);
                        if (!metricDataMapValue.colorSeverity || metricDataColorSeverity > metricDataMapValue.colorSeverity) {
                            metricDataMapValue.colorSeverity = metricDataColorSeverity;
                        }
                        const highlightColor = this.getMetricColorFromColorSeverity(metricDataColorSeverity);
                        metricDataMapValue.metricData.push({ timestamp: dataValue.timestamp, metricName: metricName, metricLabel: m.label, value: dataValue.value, unit: m.unit, filter: m.filter, filterArg: m.filterArg, highlightColor: highlightColor });
                    } else if (!config?.showChangesOnly) {
                        metricDataMapValue.metricData.push({ timestamp: dataValue.timestamp, metricName: metricName, metricLabel: m.label, value: dataValue.value, unit: m.unit, filter: m.filter, filterArg: m.filterArg, highlightColor: null });
                    }
                    previousValue = dataValue;
                }
            });
        });
        let data: ChartData[] = [];
        let categoryMap: { [group: string]: { count: number, label: string } } = {};
        Object.keys(metricDataMap).forEach(group => {
            Object.keys(metricDataMap[group]).forEach(dateTimestamp => {
                const highlightTotal = Object.values(metricDataMap[group][dateTimestamp].metricTooltipInfoMap).reduce((sum, curr) => sum + curr.highlightCount, 0);
                const color = this.getMetricColor(highlightTotal, metricDataMap[group][dateTimestamp]);
                const tooltipText = this.getMetricTooltipText(metricDataMap[group][dateTimestamp]);
                const dataValue: ChartDataValue = { color: color, highlightCount: highlightTotal, tooltipText: tooltipText, events: null, metricData: metricDataMap[group][dateTimestamp].metricData, metricDetailsArray: metricDataMap[group][dateTimestamp].metricDetailsArray };
                data.push({ category: group, dateTimestamp: dateTimestamp, value: dataValue, categoryLabel: group });
                if (!categoryMap[group]) {
                    categoryMap[group] = { count: highlightTotal, label: group };
                } else {
                    categoryMap[group].count += highlightTotal;
                }
            });
        });
        const valueSeverityMetrics = metrics.filter(m => m.config?.highlight == DailySummaryGridWidgetHighlightType.VALUE_SEVERITY);
        valueSeverityMetrics.forEach(m => {
            const metricName = m.metric.name;
            let previousColor = null;
            data.filter(d => d.category == m.label).forEach((d, index) => {
                if (d.value.color == this.emptyCellColor && !d.value.metricData.length) {
                    if (index == 0) {
                        const prevousDataSeverity = previousValuesMap[metricName] != null ? this.getSeverity(m.metric, previousValuesMap[metricName].value, this.metricDynamicRangeValues) : null;
                        if ([ValueRangeSeverity.CRITICAL, ValueRangeSeverity.WARNING].includes(prevousDataSeverity)) {
                            const previousMetricDataColorSeverity = this.getMetricDataColorSeverity(prevousDataSeverity, m.config);
                            d.value.color = this.getMetricColorFromColorSeverity(previousMetricDataColorSeverity);
                        }
                    } else {
                        d.value.color = previousColor;
                    }
                }
                previousColor = d.value.color;
            });
        });
        const sortedCategories = this.getSortedCategories(categoryMap, config);
        return { data: data, yAxisValues: sortedCategories };
    }

    private getMetricColor(highlightCount: number, value: MetricDataMapValue): string {
        if (highlightCount > 0) {
            return this.getMetricColorFromColorSeverity(value.colorSeverity);
        } else {
            return this.emptyCellColor;
        }
    }

    private getMetricColorFromColorSeverity(colorSeverity: MetricDataColorSeverity): string {
        switch (colorSeverity) {
            case MetricDataColorSeverity.NO_SEVERITY:
                return this.metricHighlightColor;
            case MetricDataColorSeverity.WARNING:
                return this.getSeverityColor('--severities-warning', this.colorManagerService.getColor('WARNING') || '#F28A29');
            case MetricDataColorSeverity.CRITICAL:
                return this.getSeverityColor('--severities-critical', this.colorManagerService.getColor('CRITICAL') || '#DF2316');
            default:
                return null;
        }
    }

    // Colors are resolved here because amCharts renders on canvas and cannot use var(--x).
    // The lookup is cached since it runs for every cell.
    private getSeverityColor(variable: string, fallback: string): string {
        if (!this.authenticationService.isNewStyleManagerEnabled() || !this.styleElement) {
            return fallback;
        }
        if (!(variable in this.severityColorCache)) {
            this.severityColorCache[variable] = getComputedStyle(this.styleElement).getPropertyValue(variable).trim();
        }
        return this.severityColorCache[variable] || fallback;
    }

    private getMetricTooltipText(value: MetricDataMapValue): string {
        if (Object.keys(value.metricTooltipInfoMap).length > 0) {
            let tooltipParts = [];
            Object.values(value.metricTooltipInfoMap).sort((a, b) => (a.label > b.label) ? 1 : -1).forEach(v => {
                tooltipParts.push(v.label + ': ' + v.highlightCount);
            });
            return tooltipParts.join('\n');
        } else {
            return null;
        }
    }

    private isMetricDataHighlight(currentValue: Value, previousValue: Value, severity: ValueRangeSeverity, config: DailySummaryGridWidgetMetricConfig): boolean {
        if (config?.highlight == DailySummaryGridWidgetHighlightType.VALUE_SEVERITY) {
            return [ValueRangeSeverity.CRITICAL, ValueRangeSeverity.WARNING].includes(severity);
        } else {
            return currentValue.value != previousValue?.value;
        }
    }

    private getSeverity(metric: Metric, value: any, dynamicValues: { [metricId: string]: any }): ValueRangeSeverity {
        if (value != null) {
            if (metric?.dictionary && metric.dictionary.length > 0) {
                const dictionatyItem = metric.dictionary.find(dictionaryItem => value.toString() == dictionaryItem.value);
                if (dictionatyItem) {
                    return dictionatyItem.severity;
                }
            } else if (metric?.ranges && metric.ranges.length > 0) {
                if (!isNaN(Number(value))) {
                    let min: number, max: number;

                    metric = metric as Metric;
                    min = metric.minMetricId ? (dynamicValues && dynamicValues[metric.minMetricId] != null ? dynamicValues[metric.minMetricId] : metric.min) : metric.min;
                    max = metric.maxMetricId ? (dynamicValues && dynamicValues[metric.maxMetricId] != null ? dynamicValues[metric.maxMetricId] : metric.max) : metric.max;
                    const rangeIndex = this.getRangeIndex(min, max, metric.ranges, value, dynamicValues);
                    if (rangeIndex != null) {
                        return metric.ranges[rangeIndex].severity as any;
                    }
                }
            }
        }
        return null;
    }

    private getRangeIndex(min: number, max: number, ranges: MetricRange[], value: any, dynamicValues: { [metricId: string]: any }): number {
        if (min != null && value < min) {
            return null;
        }
        if (max != null && value > max) {
            return null;
        }
        let rangeIndex = ranges.findIndex(range => {
            const to = range.toMetricId ? (dynamicValues[range.toMetricId] != null ? dynamicValues[range.toMetricId] : range.to) : range.to;
            return value <= to;
        });
        return rangeIndex > -1 ? rangeIndex : (ranges.length - 1);
    }

    private setMetricRangeDynamicValues(metric: Metric, allMetrics: Metric[], thing: Thing): Promise<any> {
        let promises = [];
        if (metric.minMetricId) {
            const minMetric = allMetrics.find(m => m.id == metric.minMetricId);
            promises.push(this.dataService.getLastValueByThingIdAndMetricName(thing.id, minMetric.name)
                .then(result => this.metricDynamicRangeValues[metric.minMetricId] = result?.value));
        }
        if (metric.maxMetricId) {
            const maxMetric = allMetrics.find(m => m.id == metric.maxMetricId);
            promises.push(this.dataService.getLastValueByThingIdAndMetricName(thing.id, maxMetric.name)
                .then(result => this.metricDynamicRangeValues[metric.maxMetricId] = result?.value));
        }
        return Promise.resolve(promises);
    };

    private getMetricDataColorSeverity(severity: ValueRangeSeverity, config: DailySummaryGridWidgetMetricConfig): MetricDataColorSeverity {
        if (config?.highlight == DailySummaryGridWidgetHighlightType.VALUE_SEVERITY) {
            switch (severity) {
                case ValueRangeSeverity.WARNING:
                    return MetricDataColorSeverity.WARNING;
                case ValueRangeSeverity.CRITICAL:
                    return MetricDataColorSeverity.CRITICAL;
                default:
                    return MetricDataColorSeverity.NO_SEVERITY;
            }
        } else {
            return MetricDataColorSeverity.NO_SEVERITY;
        }
    }

}

class EventDataMapValue {
    event: Alert;
    highlightCount: number;
    events: Alert[];
}

enum MetricDataColorSeverity {  // order is important
    NO_SEVERITY,
    WARNING,
    CRITICAL
}

class MetricDataMapValue {
    metricTooltipInfoMap: { [name: string]: { highlightCount: number, label: string } };
    metricData: MetricData[];
    colorSeverity: MetricDataColorSeverity;
    metricDetailsArray: MetricDetails[];
}